// Tipos da Análise de Estoque e Planejamento de Compras
// (espelham os DTOs do ERPApi — internal/core/domain/stock_analytics.go)

export interface StockAnalyticsParameters {
  service_level_percent: number;
  review_cycle_days: number;
  slow_moving_days: number;
  very_slow_moving_days: number;
  idle_days: number;
  demand_window_months: number;
  abc_window_months: number;
  abc_a_threshold_percent: number;
  abc_b_threshold_percent: number;
  xyz_x_threshold: number;
  xyz_y_threshold: number;
  excess_coverage_days: number;
  min_seasonality_months: number;
  lead_time_window_months: number;
  min_lead_time_samples: number;
  default_lead_time_days: number;
  monthly_recalc_window_months: number;
  last_update_user?: string | null;
  update_timestamp?: string | null;
}

export interface ProductMonthlySales {
  product_id: number;
  month_start: string;
  qty_sold: number;
  qty_returned: number;
  qty_net: number;
  qty_gift: number;
  gross_revenue: number;
  orders_count: number;
  avg_stock_qty: number | null;
  stockout_days: number | null;
  snapshot_days: number;
}

// Classes de giro (SlowMovingClass)
export const SLOW_MOVING_LABELS: Record<number, string> = {
  0: 'Normal',
  1: 'Baixo giro',
  2: 'Muito baixo giro',
  3: 'Parado',
  4: 'Sem histórico',
};

// Fonte do lead time (LeadTimeSource)
export const LEAD_TIME_SOURCE_LABELS: Record<number, string> = {
  0: 'Produto',
  1: 'Fornecedor',
  2: 'Global',
  3: 'Padrão',
};

export interface StockMetricsRow {
  product_id: number;
  execution_date: string;
  calculated_at: string;
  sku: string;
  description: string | null;
  provider_name: string | null;

  current_stock_qty: number;
  current_stock_available_qty: number;
  on_order_qty: number;
  current_min_qty: number | null;
  current_max_qty: number | null;
  unit_cost: number | null;
  default_provider_id: number | null;

  avg_daily_demand: number | null;
  demand_std_dev_daily: number | null;
  demand_cv: number | null;
  qty_sold_30d: number | null;
  qty_sold_90d: number | null;
  qty_sold_365d: number | null;
  last_sale_date: string | null;
  days_since_last_sale: number | null;

  avg_stock_90d: number | null;
  avg_stock_365d: number | null;
  turnover_90d_annualized: number | null;
  turnover_365d: number | null;
  turnover_cost_365d: number | null;
  coverage_days: number | null;
  stockout_days_365d: number | null;

  slow_moving_class: number;
  abc_class: string | null;
  xyz_class: string | null;
  seasonal_index_json: string | null;

  lead_time_avg_days: number | null;
  lead_time_std_dev_days: number | null;
  lead_time_samples: number;
  lead_time_source: number;

  safety_stock_qty: number | null;
  reorder_point_qty: number | null;
  suggested_min_qty: number | null;
  suggested_max_qty: number | null;
  suggested_order_qty: number | null;

  excess_stock_flag: boolean;
  excess_stock_value: number | null;
  stockout_risk_flag: boolean;

  rationale_json: string | null;
}

export interface StockAnalyticsSummary {
  products_analyzed: number;
  stockout_risk_count: number;
  slow_moving_count: number;
  idle_count: number;
  no_history_count: number;
  excess_count: number;
  excess_value_total: number;
  /** Σ estoque físico × preço de compra do fornecedor vigente. */
  stock_purchase_cost_total: number;
  /** Σ estoque físico × preço de venda cadastrado do produto. */
  stock_sale_value_total: number;
  avg_turnover_365d: number | null;
  abc_counts: Record<string, number>;
  xyz_counts: Record<string, number>;
  last_calculated_at: string | null;
}

export interface ProductHistoryResponse {
  product_id: number;
  sku: string;
  description: string | null;
  monthly: ProductMonthlySales[] | null;
  seasonal_indexes?: number[];
}

export interface PurchasePlanItem {
  product_id: number;
  sku: string;
  description: string | null;
  current_stock_qty: number; // físico
  current_stock_available_qty: number; // físico − reservas de pedidos sem baixa (base da compra)
  on_order_qty: number;
  coverage_days: number | null;
  lead_time_avg_days: number | null;
  lead_time_std_dev_days: number | null;
  lead_time_source: number;
  avg_daily_demand: number | null;
  forecast_cycle_qty: number | null;
  suggested_order_qty: number;
  provider_price: number | null;
  unit_cost: number | null;
  estimated_value: number;
  stockout_risk_flag: boolean;
  rationale_json: string | null;
}

export interface PurchasePlanProviderGroup {
  provider_id: number | null;
  provider_name: string;
  items: PurchasePlanItem[];
  total_items: number;
  total_qty: number;
  estimated_total: number;
}

export interface PurchasePlanningResponse {
  providers: PurchasePlanProviderGroup[] | null;
  providers_count: number;
  items_count: number;
  estimated_total: number;
  last_calculated_at: string | null;
}

/** Prévia do pedido de compra para X meses de um fornecedor (RN-EST-75). */
export interface PurchasePlanHorizonItem {
  product_id: number;
  sku: string;
  description: string | null;
  current_stock_qty: number;
  current_stock_available_qty: number;
  on_order_qty: number;
  avg_daily_demand: number | null;
  lead_time_avg_days: number | null;
  forecast_horizon_qty: number; // demanda prevista em LT + horizonte
  safety_stock_qty: number;
  suggested_qty: number;
  provider_price: number | null;
  unit_cost: number | null;
  estimated_value: number;
}

export interface PurchasePlanHorizonResponse {
  provider_id: number;
  provider_name: string;
  months: number;
  horizon_days: number;
  items: PurchasePlanHorizonItem[];
  items_count: number;
  suggested_count: number;
  estimated_total: number;
  last_calculated_at: string | null;
}

export interface ApplySuggestionItem {
  product_id: number;
  min_stock_quantity: number;
  max_stock_quantity: number;
}

export interface ApplySuggestionItemResult {
  product_id: number;
  success: boolean;
  error?: string;
}

export interface ApplySuggestionsResponse {
  applied: number;
  failed: number;
  results: ApplySuggestionItemResult[];
}

export interface StockMetricsFilter {
  page?: number;
  limit?: number;
  search?: string;
  abc_class?: string;
  xyz_class?: string;
  slow_moving_class?: number | null;
  provider_id?: number | null;
  excess_only?: boolean;
  stockout_risk_only?: boolean;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

// Racional de cálculo (RationaleJson desserializado)
export interface MetricRationale {
  demanda: {
    formula: string;
    janela_meses: number;
    meses_com_dados: number;
    demanda_total_janela: number;
    media_diaria: number;
    desvio_padrao_diario: number;
    coeficiente_variacao: number;
  };
  sazonalidade: {
    formula: string;
    aplicada: boolean;
    motivo?: string;
    indices?: number[];
    fator_horizonte: number;
  };
  lead_time: {
    formula: string;
    fonte: string;
    media_dias: number;
    desvio_dias: number;
    amostras: number;
  };
  estoque_seguranca: {
    formula: string;
    nivel_servico_percent: number;
    fator_z: number;
    estoque_seguranca: number;
    ponto_pedido: number;
  };
  sugestoes: {
    formula_min: string;
    formula_max: string;
    formula_compra: string;
    ciclo_revisao_dias: number;
    demanda_diaria_prevista: number;
    min_sugerido: number | null;
    max_sugerido: number | null;
    compra_sugerida: number | null;
    estoque_disponivel: number;
    qtd_em_pedido: number;
  };
  qualidade_dados: {
    meses_historico: number;
    cobertura_snapshot_percent: number;
    confianca: string;
  };
  observacoes?: string[];
}
